/**
 * Typed bindings for /api/stats — dashboard KPIs + recent activity.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export interface CountByKey {
  key: string;
  count: number;
}

export interface AccountsBlock {
  total: number;
  by_status: CountByKey[];
  by_role: CountByKey[];
  permanent_peer_flood: number;
  in_flood_wait: number;
  spam_limited: number;
  // Banned vs frozen split the way the accounts list's status badge does it.
  banned: number;
  frozen: number;
}

export interface ProxiesBlock {
  total: number;
  alive: number;
  dead: number;
  untested: number;
}

export interface ChannelsBlock {
  total: number;
  by_status: CountByKey[];
  by_source: CountByKey[];
}

export interface ScrapedUsersBlock {
  total: number;
  bots: number;
  with_username: number;
  premium: number;
  by_last_online_kind: CountByKey[];
}

/** One row per campaign strategy — manual_add / dm_message / channel_forward
 * / story_mention. Lets the dashboard label outreach work correctly instead of
 * calling every send an "invite". */
export interface StrategyBlock {
  strategy: string;
  campaigns: number;
  active: number;
  targets: number;
  attempted: number;
  succeeded: number;
  failed: number;
  /** Distinct people reached (not attempts — the same user can be in several
   * campaigns). */
  people_reached: number;
}

export interface InboxBlock {
  dialogs: number;
  unread: number;
  incoming_7d: number;
}

export interface InvitesBlock {
  total_campaigns: number;
  by_status: CountByKey[];
  /** Campaigns the system paused itself, with a reason, awaiting the operator. */
  stopped_with_error?: number;
  cumulative_attempts: number;
  cumulative_added: number;
  cumulative_failed: number;
  by_strategy: StrategyBlock[];
}

export interface StatsOverview {
  accounts: AccountsBlock;
  proxies: ProxiesBlock;
  channels: ChannelsBlock;
  scraped_users: ScrapedUsersBlock;
  invites: InvitesBlock;
  inbox: InboxBlock;
}

export interface AccountHealth {
  id: string;
  display_name: string;
  status: string;
  role: string;
  proxy_label: string | null;
  flood_wait_until: string | null;
  total_invites_sent: number;
  total_floodwaits: number;
  peer_flood_count: number;
  is_peer_flood_permanent: boolean;
  last_used_at: string | null;
  last_check_at: string | null;
  last_check_error: string | null;
}

export interface RecentScrapeJob {
  id: string;
  channel_username: string | null;
  channel_title: string | null;
  status: string;
  users_added: number;
  users_seen: number;
  started_at: string | null;
  finished_at: string | null;
}

export interface RecentCampaign {
  id: string;
  name: string;
  target_channel_input: string;
  strategy: string;
  status: string;
  succeeded: number;
  attempted: number;
  total_targets: number;
  started_at: string | null;
  finished_at: string | null;
}

export interface RecentActivity {
  scrape_jobs: RecentScrapeJob[];
  campaigns: RecentCampaign[];
}

export function useStatsOverview() {
  return useQuery<StatsOverview>({
    queryKey: ["stats", "overview"],
    queryFn: () => api.get<StatsOverview>("/api/stats/overview"),
    refetchInterval: 30_000,
  });
}

export function useAccountHealth() {
  return useQuery<AccountHealth[]>({
    queryKey: ["stats", "account_health"],
    queryFn: () => api.get<AccountHealth[]>("/api/stats/account_health"),
    refetchInterval: 30_000,
  });
}

export function useRecentActivity() {
  return useQuery<RecentActivity>({
    queryKey: ["stats", "recent_activity"],
    queryFn: () => api.get<RecentActivity>("/api/stats/recent_activity"),
    refetchInterval: 30_000,
  });
}

export interface SellerStats {
  source_label: string;
  total: number;
  active: number;
  banned: number;
  flood_wait: number;
  peer_flood_perm: number;
  avg_cost_usd: number | null;
  total_cost_usd: number;
  cost_per_active_usd: number | null;
  survival_rate: number;
  avg_age_days: number | null;
}

export interface WarmupSpeedStats {
  speed: string;
  total: number;
  active: number;
  banned: number;
  flood_wait: number;
  peer_flood_perm: number;
  completed: number;
  survival_rate: number;
}

export interface FailingActionStats {
  action_type: string;
  failed: number;
  completed: number;
  fail_rate: number;
}

export interface InviteOutcomeStats {
  status: string;
  count: number;
}

export interface AgeVsBanBucket {
  bucket: string;
  total: number;
  banned_or_pf: number;
  survival_rate: number;
}

export interface CostSummary {
  total_spent_usd: number;
  accounts_with_cost: number;
  avg_cost_active_usd: number | null;
  avg_cost_dead_usd: number | null;
  burned_dollars: number;
}

export interface AnalyticsResponse {
  sellers: SellerStats[];
  warmup_speeds: WarmupSpeedStats[];
  failing_actions: FailingActionStats[];
  invite_outcomes: InviteOutcomeStats[];
  age_buckets: AgeVsBanBucket[];
  cost: CostSummary;
  top_ban_reasons: CountByKey[];
}

export function useAnalytics() {
  return useQuery<AnalyticsResponse>({
    queryKey: ["stats", "analytics"],
    queryFn: () => api.get<AnalyticsResponse>("/api/stats/analytics"),
    refetchInterval: 60_000,
  });
}

// --- Business analytics (money + invite economics) -------------------------

export interface BusinessKpis {
  total_spent_usd: number;
  burned_usd: number;
  accounts_with_cost: number;
  avg_account_cost_usd: number | null;
  inviter_accounts: number;
  invites_sent_total: number;
  avg_invites_per_account: number | null;
  attempts_total: number;
  leads: number;
  conversion_rate: number | null;
  cost_per_lead_usd: number | null;
  audience_total: number;
}

export interface DailyPoint {
  day: string;
  attempts: number;
  leads: number;
}

export interface SellerCpl {
  source_label: string;
  leads: number;
  spend_usd: number;
  cost_per_lead_usd: number | null;
}

export interface BusinessAnalytics {
  days: number;
  kpis: BusinessKpis;
  invites_daily: DailyPoint[];
  outcomes: InviteOutcomeStats[];
  cost_per_lead_by_seller: SellerCpl[];
}

export function useBusinessAnalytics(days = 30) {
  return useQuery<BusinessAnalytics>({
    queryKey: ["stats", "business", days],
    queryFn: () => api.get<BusinessAnalytics>(`/api/stats/business?days=${days}`),
    refetchInterval: 60_000,
  });
}
